import { Tooltip } from '@bp/ui-components';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { FC, Fragment, MouseEvent } from 'react';
import { groupByArray } from '../../../utils/arrayFunc';
import { useTeachingBlockStore } from '../Contexts/TeachingBlockContext';
import { cardMatchesSearch } from '../cardSearch';
import { TeachingBlockCard } from '../TeachingBlockCard/TeachingBlockCard';
import { TeachingBlockCardType } from '../types';
import styles from './CardPicker.module.scss';

export const CardPicker: FC = observer(() => {
  const store = useTeachingBlockStore();

  const matchesSearch = (card: TeachingBlockCardType) => cardMatchesSearch(card, store.cardSearchString);

  const getCards = () => {
    if (store.gridContext === 'rooms') {
      return store.allPlacedCards.filter((c) => c.rooms.length === 0).filter(matchesSearch);
    }
    return (
      store.selectedRows.length > 0
        ? store.freeCards.filter((card) => {
            return store.selectedRows.some((row) => {
              return store.isUuidPresentInTimetableCard(card, row.value);
            });
          })
        : store.freeCards
    ).filter(matchesSearch);
  };

  const bySubjectName = (a: TeachingBlockCardType[], b: TeachingBlockCardType[]) =>
    (a[0].subject?.name ?? '').localeCompare(b[0].subject?.name ?? '');

  // Raum-Kontext: KEINE Gruppierung – jede platzierte Karte steht für sich (sie hat eine eigene
  // Zeit-Position, die per Hover angezeigt wird). Sonst wie gehabt nach Fach + Dauer stapeln.
  let stacks: TeachingBlockCardType[][];
  if (store.gridContext === 'rooms') {
    stacks = getCards()
      .map((card) => [card])
      .sort(bySubjectName);
  } else {
    const cards: TeachingBlockCardType[][] = [];
    const lessonGroups = groupByArray(getCards(), (card) => card.lesson?.uuid);
    lessonGroups.forEach((lessonGroup) => {
      const durationGroups = groupByArray(lessonGroup, (group) => group.duration);
      durationGroups.forEach((durationGroup) => cards.push(durationGroup));
    });
    stacks = cards.sort(bySubjectName);
  }

  // Gesteckte Position (Zeitraum) der Karte – im Raum-Kontext als schneller Hover-Tooltip.
  const positionHint = (card: TeachingBlockCardType): string | undefined => {
    if (store.gridContext !== 'rooms' || !card.startDate || !card.endDate) return undefined;
    return `${dayjs(card.startDate).format('DD.MM.YYYY')} – ${dayjs(card.endDate).format('DD.MM.YYYY')}`;
  };

  const onCardClick = (event: MouseEvent<HTMLDivElement>, card: TeachingBlockCardType) => {
    event.stopPropagation();
    event.preventDefault();
    if (!card.locked) {
      // Raum-Kontext: Shift beim Aufnehmen → Kopiermodus (Karte in mehrere Räume setzen).
      store.pickCard(card, { x: event.clientX, y: event.clientY }, true, undefined, event.shiftKey);
    }
  };

  return (
    <div className={classNames(styles['card-picker'], 'has-scrollbar', 'drop-and-delete')}>
      {stacks.map((container) => {
        const card = container[0];
        const cardClasses = classNames({
          [styles.hidden]: container.length === 1 && store.pickedCard?.card === card,
          [styles.multi]: container.length >= 2,
        });
        const hint = positionHint(card);

        const cardEl = (
          <TeachingBlockCard
            className={cardClasses}
            card={{
              ...toJS(card),
              availabilityWarnings: [],
            }}
            count={container.length}
            hideTitle={!!hint}
            onClick={onCardClick}
            onHover={(isHovering, card) => {
              if (!isHovering && store.pickedCard) {
                store.selectCard(store.pickedCard.card);
              } else {
                store.selectCard(card);
              }
            }}
            passive={false}
          />
        );

        // Tooltip (ui-components) ist "teuer" → nur im Raum-Kontext rendern, wo die gesteckte
        // Position gebraucht wird. Sonst die Karte unverändert ausgeben.
        return hint ? (
          <Tooltip key={card.uuid} content={hint}>
            {cardEl}
          </Tooltip>
        ) : (
          <Fragment key={card.uuid}>{cardEl}</Fragment>
        );
      })}
    </div>
  );
});
